import React, { useState, useEffect } from "react";
import {
  IconButton,
  Popover,
  Tooltip,
  Box,
  Typography,
  Switch,
  Slider,
  Paper,
} from "@mui/material";
import AbcIcon from "@mui/icons-material/Abc";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import TextFieldsIcon from "@mui/icons-material/TextFields";

const OpenDyslexic = () => {
  const [useOpenDyslexic, setUseOpenDyslexic] = useState(() => {
    return localStorage.getItem("useOpenDyslexic") === "true";
  });

  const [fontSize, setFontSize] = useState(() => {
    const savedSize = localStorage.getItem("dyslexicFontSize");
    return savedSize ? parseInt(savedSize) : 22;
  });

  // Popover state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Load font dynamically when needed
  useEffect(() => {
    if (useOpenDyslexic && !document.getElementById("opendyslexic-font")) {
      const link = document.createElement("link");
      link.id = "opendyslexic-font";
      link.rel = "stylesheet";
      link.href = "https://fonts.cdnfonts.com/css/opendyslexic";
      document.head.appendChild(link);
    }
  }, [useOpenDyslexic]);

  // Toggle OpenDyslexic
  const toggleOpenDyslexic = () => {
    const newMode = !useOpenDyslexic;
    setUseOpenDyslexic(newMode);
    localStorage.setItem("useOpenDyslexic", newMode.toString());

    // Apply font to the document
    if (newMode) {
      document.body.classList.add("opendyslexic-enabled");
      applyStyles();
    } else {
      document.body.classList.remove("opendyslexic-enabled");
      removeStyles();
    }
  };

  // Handle font size change
  const handleFontSizeChange = (event, newValue) => {
    setFontSize(newValue);
    localStorage.setItem("dyslexicFontSize", newValue.toString());
    if (useOpenDyslexic) {
      applyStyles();
    }
  };

  // Open popover
  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close popover
  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  // Apply styles to relevant elements
  const applyStyles = () => {
    const cardTexts = document.querySelectorAll(".card-text");
    cardTexts.forEach((el) => {
      el.style.fontFamily = "'OpenDyslexic', sans-serif";
      el.style.fontSize = `${fontSize}px`;
    });
  };

  // Remove styles
  const removeStyles = () => {
    const cardTexts = document.querySelectorAll(".card-text");
    cardTexts.forEach((el) => {
      el.style.fontFamily = "";
      el.style.fontSize = "";
    });
  };

  // Apply styles on mount if enabled
  useEffect(() => {
    if (useOpenDyslexic) {
      applyStyles();
    }
    // Clean up
    return () => {
      removeStyles();
    };
  }, [fontSize, useOpenDyslexic]);

  return (
    <>
      <Tooltip title="Aktivera OpenDyslexic">
        <IconButton
          onClick={handleOpenPopover}
          color={useOpenDyslexic ? "primary" : "default"}
          size="small"
          aria-label="Tillgänglighet"
        >
          <TextFieldsIcon
            sx={{
              fontSize: { xs: "1.5rem", md: "1.7rem" },
              color: "text.secondary",
            }}
          />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: 4,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Paper sx={{ p: 2, width: 280, borderRadius: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Tillgänglighet
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="body2">OpenDyslexic</Typography>
            <Switch
              checked={useOpenDyslexic}
              onChange={toggleOpenDyslexic}
              color="primary"
              size="small"
            />
          </Box>

          {useOpenDyslexic && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormatSizeIcon
                sx={{ mr: 1, fontSize: "1.2rem", color: "text.secondary" }}
              />
              <Slider
                value={fontSize}
                onChange={handleFontSizeChange}
                aria-labelledby="font-size-slider"
                valueLabelDisplay="auto"
                step={1}
                min={16}
                max={32}
                sx={{
                  height: 4,
                  "& .MuiSlider-thumb": {
                    width: 14,
                    height: 14,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ ml: 1, minWidth: "24px", textAlign: "right" }}
              >
                {fontSize}
              </Typography>
            </Box>
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default OpenDyslexic;
